import { ChangeEvent, useEffect } from 'react';
import styled from '@emotion/styled';
import { GETMediaFilesResponse } from '@myme/api';
import { MediaBlobType, Pagination as IPagination } from '@myme/utils';
import { useDebounce, useTranslation } from 'src/hooks';
import { Pagination } from 'src/components/atoms';
import { Flex, GridContainer, GridItem } from 'src/components/layout';
import { ContainerHeader } from './ContainerHeader';
import { DeleteSingleFileDialog } from './DeleteSingleFileDialog';
import { MediaContainerActions } from './MediaContainerActions';
import { MediaContainerSkeleton } from './MediaContainerSkeleton';
import { useMediaContext } from './MediaContext';
import { MediaFilesNotFound } from './MediaFilesNotFound';
import { SingleFilePreview } from './SingleFilePreview';

interface LibraryProps {
  type: 'LIBRARY';
  media: GETMediaFilesResponse;
  mediaType: 'ALL';
  searchField: string;
  setSearchField: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  pagination: IPagination;
  onPageChange: (page: number) => void;
  getLastPageNumber: (total: number) => number;
}

interface EditorProps {
  type: 'EDITOR';
  media: GETMediaFilesResponse;
  mediaType: MediaBlobType;
  searchField: string;
  setSearchField: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  onInsertAction: () => void;
  pagination: IPagination;
  onPageChange: (page: number) => void;
  getLastPageNumber: (total: number) => number;
}

type Props = LibraryProps | EditorProps;

const StyledMediaContainer = styled.div(
  props => `
    display: flex;
    flex-direction: column;
    padding: 2rem;
    border-radius: .75rem;
    background-color: ${props.theme.palette.secondary.main};
    margin: 0 auto;
    `
);

// MediaContainer needs to be wrapped with MediaContextProvider
export const MediaContainer = ({
  media,
  isLoading,
  pagination,
  searchField,
  setSearchField,
  onPageChange,
  getLastPageNumber,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { fileUUID, removeFileDialog, handleSelectFile, handleClickAway } = useMediaContext();
  const debouncedMediaSearchField = useDebounce(searchField, 500);
  const mediaNotFound = media.total === 0 && !isLoading;
  const searchResultsNotFound = media.total === 0 && !mediaNotFound;

  const handleSearchFieldChange = (e: ChangeEvent<HTMLInputElement>) => setSearchField(e.target.value);

  useEffect(() => {
    onPageChange(1);
  }, [debouncedMediaSearchField]);

  return (
    <>
      <StyledMediaContainer>
        <ContainerHeader
          text={t('pages.mymes.editor.blobs.library.media_container_header')}
          searchField={searchField}
          onSearchFieldChange={handleSearchFieldChange}
        />
        <MediaFilesNotFound filesNotFound={mediaNotFound} isSearchResultNotFound={searchResultsNotFound} />
        {!isLoading ? (
          <GridContainer spacing={1} onClick={handleClickAway}>
            {media.rows.map(file => (
              <GridItem key={file.fileName}>
                <SingleFilePreview {...file} fileUUID={fileUUID} onFileSelect={handleSelectFile} />
              </GridItem>
            ))}
          </GridContainer>
        ) : (
          <MediaContainerSkeleton />
        )}
        {getLastPageNumber(media.total) > 1 && (
          <Flex justifyContent='flex-end' mt={2}>
            <Pagination
              count={getLastPageNumber(media.total)}
              siblingCount={0}
              page={pagination.page}
              color='primary'
              onChange={(e: ChangeEvent<unknown>, value: number) => onPageChange(value)}
            />
          </Flex>
        )}

        <MediaContainerActions media={media.rows} isLoading={isLoading} {...props} />
      </StyledMediaContainer>
      {removeFileDialog.isOpen && fileUUID && (
        <DeleteSingleFileDialog fileUUID={fileUUID} onClose={removeFileDialog.closeDialog} />
      )}
    </>
  );
};
